<template>
  <div class="stripe-payment-form">
    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
    </div>
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <p class="text-red-600 text-sm">{{ error }}</p>
    </div>
    <div v-show="!loading && !error && !success" class="payment-element-container">
      <div id="payment-element" class="mb-4"></div>
      <a-button :loading="isProcessing" :disabled="isProcessing" class="w-full secondary_medium" @click="handleSubmit">
        {{ isProcessing ? 'Saving...' : 'Save Payment Method' }}
      </a-button>
    </div>
    <div v-if="success" class="bg-green-50 border border-green-200 rounded-lg p-4">
      <p class="text-green-600 text-sm">Payment method saved successfully!</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../../../env.config';
import DashboardServiceV3 from '@/services/DashboardServiceV3';

const emit = defineEmits(['success', 'error']);

const loading = ref(true);
const error = ref(null);
const success = ref(false);
const isProcessing = ref(false);
const stripe = ref(null);
const elements = ref(null);
const paymentElement = ref(null);

const initializeStripe = async () => {
  try {
    loading.value = true;
    error.value = null;

    // Create setup intent from backend
    const response = await DashboardServiceV3.createSetupIntent();

    if (!response?.success || !response.client_secret) {
      throw new Error('Failed to create setup intent');
    }

    const clientSecret = response.client_secret;

    // Initialize Stripe
    stripe.value = await loadStripe(STRIPE_PUBLISHABLE_KEY);

    // Create Elements instance
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#7c3aed',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    };

    elements.value = stripe.value.elements({
      clientSecret,
      appearance,
    });

    // Create and mount Payment Element
    paymentElement.value = elements.value.create('payment', {
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
        radios: true,
        spacedAccordionItems: true,
      },
    });

    paymentElement.value.mount('#payment-element');

    loading.value = false;
  } catch (err) {
    console.error('Error initializing Stripe:', err);
    error.value = err.message || 'Failed to initialize payment form';
    loading.value = false;
    emit('error', err);
  }
};

const handleSubmit = async () => {
  if (!stripe.value || !elements.value) return;

  isProcessing.value = true;
  error.value = null;

  try {
    const { error: submitError } = await elements.value.submit();

    if (submitError) {
      throw submitError;
    }

    const { error: confirmError } = await stripe.value.confirmSetup({
      elements: elements.value,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      throw confirmError;
    }

    success.value = true;
    emit('success');
  } catch (err) {
    console.error('Error saving payment method:', err);
    error.value = err.message || 'Failed to save payment method';
    emit('error', err);
  } finally {
    isProcessing.value = false;
  }
};

onMounted(() => {
  initializeStripe();
});

onUnmounted(() => {
  if (paymentElement.value) {
    paymentElement.value.destroy();
  }
});
</script>

<style scoped>
.stripe-payment-form {
  width: 100%;
}

.payment-element-container {
  width: 100%;
}

#payment-element {
  min-height: 200px;
}
</style>
